import { css } from '@emotion/react';
import { Stack } from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';
import { CSSProperties, PropsWithChildren, ReactNode, useState } from 'react';
import { SearchInput } from '../../../../CoreComponents/base/SearchInput';
import { ContainerName } from '../../../../constants/styles';
import { ICaptableDataModel } from '../../../../data-models/captable2.data-model';
import { CTInvestmentDetails } from '../InvestmentDetails/CTInvestmentDetails';
import { CaptableGroupingMode } from '../SummaryGrid2/CaptableSummaryColDefs2';
import { CaptableSummaryGrid2 } from '../SummaryGrid2/CaptableSummaryGrid2';
import { CaptableActionsMenu } from './CaptableActionsMenu';
import { CaptableSummaryGrid, StakeholderType } from './CaptableSummaryGrid';
import { GroupingToggle } from './GroupingToggle';
import { InvestmentsFilter } from './InvestmentsFilter';
import { CaptableViewMode, ViewModeToggle } from './ViewModeToggle';

const MotionStack = motion(Stack);

const filterBarCss = css`
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
  justify-content: start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  @container ${ContainerName.captableContainer} (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

interface ICaptableSummaryProps {
  data: ICaptableDataModel;
}

export function CaptableSummary({ data }: ICaptableSummaryProps) {
  const [viewMode, setViewMode] = useState<CaptableViewMode>('summary');
  const [quickFilterText, setQuickFilterText] = useState('');
  const [investmentFilter, setInvestmentFilter] = useState<StakeholderType>();
  const [groupingMode, setGroupingMode] = useState<CaptableGroupingMode>(CaptableGroupingMode.stakeholder);

  let viewContent: ReactNode;
  switch (viewMode) {
    case 'summary':
      viewContent = (
        <FadeInSection key='summary' duration={0.25} style={{ marginBottom: '1rem' }}>
          <CaptableSummaryGrid2
            data={data}
            quickFilterText={quickFilterText}
            investmentFilter={investmentFilter}
            groupingMode={groupingMode}
          />
        </FadeInSection>
      );
      break;
    case 'detailed':
      viewContent = (
        <FadeInSection key='detailed' duration={0.25} style={{ marginBottom: '1rem' }}>
          <CaptableSummaryGrid
            data={data}
            quickFilterText={quickFilterText}
            investmentFilter={investmentFilter}
          />
        </FadeInSection>
      );
      break;
    case 'security':
      viewContent = (
        <FadeInSection key='security' duration={0.15}>
          <CTInvestmentDetails data={data} />
        </FadeInSection>
      );
      break;
  }

  return (
    <>
      <Stack css={filterBarCss}>
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
        <AnimatePresence>
          {(viewMode === 'summary' || viewMode === 'detailed') && (
            <AnimatedFilterRow>
              <StakeholderFilter onChange={setQuickFilterText} />

              <Stack direction={'row'} gap={'0.5rem'} alignItems={'center'} ml={'0.5rem'}>
                <InvestmentsFilter value={investmentFilter} onChange={setInvestmentFilter} />
                <AnimatePresence>
                  {viewMode === 'summary' && (
                    <motion.div
                      key='grouping-toggle'
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
                    >
                      <GroupingToggle value={groupingMode} onChange={setGroupingMode} />
                    </motion.div>
                  )}
                </AnimatePresence>
                <CaptableActionsMenu showGridViewOptions={viewMode === 'detailed'} />
              </Stack>
            </AnimatedFilterRow>
          )}
        </AnimatePresence>
      </Stack>
      <AnimatePresence mode='wait'>{viewContent}</AnimatePresence>
    </>
  );
}

interface IFadeInSectionProps extends PropsWithChildren {
  duration?: number;
  style?: CSSProperties;
}

function FadeInSection({ children, duration = 0.25, style }: IFadeInSectionProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration, ease: 'easeInOut' }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

function AnimatedFilterRow({ children }: PropsWithChildren) {
  return (
    <MotionStack
      key='grid-filters'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: 'easeInOut' }}
      flex={1}
      display={'grid'}
      gridTemplateColumns={'1fr auto'}
      alignItems={'center'}
    >
      {children}
    </MotionStack>
  );
}

export function StakeholderFilter({ onChange }: { onChange: (value: string) => void }) {
  return (
    <SearchInput
      onChange={onChange}
      style={{ width: '100%', maxWidth: '18rem', margin: 0, justifySelf: 'start' }}
      placeholder='Search for a stakeholder name'
    />
  );
}
